<template>
  <div :class="$attrs.class" :style="$attrs.style">
    <!-- no lift when open: the shadow read as a dark band on the dark theme; the divider separates it -->
    <v-card :elevation="0" v-bind="innerAttrs" class="contact-tile-card">
      <!-- two-line, no card padding: the three-line item and the card text padding made each contact ~130px tall -->
      <v-card-text class="pa-0">
        <v-list-item lines="two">
          <template v-if="$vuetify.display.smAndUp" #prepend>
            <v-avatar rounded>
              <img :src="memberAvatar" alt="avatar">
            </v-avatar>
          </template>
          <div>
            <v-list-item-title class="white-space--normal text-high-emphasis font-weight-medium">
              {{ contact.first_name }} {{ contact.last_name }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="memberCompany" class="member-company">
              {{ memberCompany }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>{{ contact.title }}</v-list-item-subtitle>
            <!-- data-test sits on this element, not on AppTooltip: v-tooltip is an overlay in
                 Vuetify 3 and renders no root element of its own for attrs to land on. -->
            <v-list-item-subtitle
              v-if="showClientAccessWarning"
              :data-test="`client-access-warning-${clientAccessWarningVariant}`"
              class="d-flex align-center flex-wrap gap-2 mt-1"
            >
              <AppTooltip
                icon="mdi-alert-circle-outline"
                :text="clientAccessWarningText"
                max-width="220px"
              />
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="sourceType === 'company' && memberRoles.length" class="mt-3">
              <CommonChip v-for="role in getContactRoles(memberRoles)" :key="role.text" class="ma-1">
                {{ role.text }}
              </CommonChip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="contact.active === false" class="mb-1">
              <CommonChip color="black" class="text-white">Inactive</CommonChip>
            </v-list-item-subtitle>
            <v-list-item-subtitle v-if="sourceType === 'deal'" class="d-flex align-center flex-wrap gap-2">
              <CommonChip v-if="contact.referring_agent">{{ $t('referring') }}</CommonChip>
              <CommonChip v-if="contact.receiving_agent">{{ $t('receiving') }}</CommonChip>
            </v-list-item-subtitle>
          </div>
          <template v-if="expandable" #append>
            <v-btn
              icon
              size="small"
              variant="text"
              class="text-medium-emphasis"
              :aria-label="showDetails ? 'Hide contact details' : 'Show contact details'"
              @click="showDetails = !showDetails"
            >
              <v-icon>{{ showDetails ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </v-card-text>
      <v-divider v-if="showDetails" />
      <v-expand-transition>
        <v-card v-if="showDetails" flat>
          <v-card-text class="pa-0 pb-2 bg-sc-surface-tint">
            <div class="px-4 pt-3 pb-1 text-caption text-medium-emphasis">Contact details</div>
            <v-btn-toggle
              v-if="showReferring"
              v-model="referringStatus"
              class="mx-4 mb-2"
            >
              <v-btn size="small" value="referring">Referring</v-btn>
              <v-btn size="small" value="receiving">Receiving</v-btn>
            </v-btn-toggle>
            <v-list-item
              v-if="memberPhone"
              :href="`tel:${memberPhone}`"
              density="compact"
              prepend-icon="mdi-phone"
            >
              <v-list-item-title>
                {{ $filters.formatPhone(memberPhone) }}
                <span v-if="memberPhoneExt" class="ml-1">Ext: {{ memberPhoneExt }}</span>
              </v-list-item-title>
              <v-list-item-subtitle>Direct</v-list-item-subtitle>
            </v-list-item>
            <v-list-item
              v-if="memberMobile"
              :href="`tel:${memberMobile}`"
              density="compact"
              prepend-icon="mdi-phone"
            >
              <v-list-item-title>{{ $filters.formatPhone(memberMobile) }}</v-list-item-title>
              <v-list-item-subtitle>Mobile</v-list-item-subtitle>
            </v-list-item>
            <v-list-item
              v-if="contact.email"
              :href="`mailto:${contact.email}`"
              density="compact"
              prepend-icon="mdi-email"
            >
              <v-list-item-title>{{ contact.email }}</v-list-item-title>
              <v-list-item-subtitle>Email</v-list-item-subtitle>
            </v-list-item>

            <template v-if="!disableUserActions">
              <div class="px-4 pt-3 pb-1 text-caption text-medium-emphasis">Actions</div>
              <!-- one row of small buttons; edit / remove keep their tooltip when the
                   source locks them, so they look disabled but stay hoverable -->
              <div class="d-flex ga-2 px-4" data-test="contact-tile-actions">
                <v-btn
                  variant="text"
                  class="flex-1-1-0"
                  prepend-icon="mdi-launch"
                  :to="getAccountRoute({ name: 'contact', params: { id: contact._key } })"
                >
                  Profile
                </v-btn>
                <v-tooltip
                  v-if="showEditAction"
                  :disabled="!isEditDisabled"
                  :text="`${sourceLabel} contacts must be edited at the ${sourceLabel} level`"
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      class="flex-1-1-0"
                      prepend-icon="mdi-pencil"
                      :class="{ 'filters-action--disabled': isEditDisabled }"
                      data-track="contact.tile-roles.edit"
                      @click="editContactRolesClickHandler"
                    >
                      Edit roles
                    </v-btn>
                  </template>
                </v-tooltip>
                <v-btn
                  v-if="sourceType === 'deal' && contact._edge"
                  variant="text"
                  class="flex-1-1-0"
                  prepend-icon="mdi-refresh"
                  :loading="refreshContactEdge === contact._edge"
                  @click="$emit('refresh:contact', contact._edge)"
                >
                  Refresh info
                </v-btn>
                <v-tooltip
                  :disabled="!isEditDisabled"
                  :text="`${sourceLabel} contacts must be removed at the ${sourceLabel} level`"
                  location="top"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      variant="text"
                      class="flex-1-1-0"
                      prepend-icon="mdi-delete"
                      :color="isEditDisabled ? undefined : 'error'"
                      :class="{ 'filters-action--disabled': isEditDisabled }"
                      data-track="contact.tile.remove"
                      @click="removeContactClickHandler(contact._key)"
                    >
                      Remove
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>
            </template>
          </v-card-text>
          <v-divider />
          <!-- one compact row, centred: collapses the details after scrolling past them -->
          <v-btn
            block
            variant="text"
            size="small"
            rounded="0"
            class="text-medium-emphasis"
            prepend-icon="mdi-chevron-up"
            data-test="contact-tile-hide-details"
            @click="showDetails = false"
          >
            Hide details
          </v-btn>
        </v-card>
      </v-expand-transition>
    </v-card>

    <v-dialog v-model="roleDialog" max-width="500">
      <v-form ref="form" @submit.prevent="saveRoles">
        <v-card>
          <v-toolbar flat>
            <v-toolbar-title>Edit Role(s)</v-toolbar-title>
            <v-spacer />
          </v-toolbar>
          <v-card-text>
            <v-select
              v-model="newRoles"
              :items="contactRoles"
              label="Select the roles for this contact"
              :rules="[(v) => v.length> 0 || 'Contact should have at least one role']"
              multiple
              chips
              autocomplete="off"
            >
              <template #chip="{ item }">
                <CommonChip closable class="ma-1" @click:close="newRoles = newRoles.filter(role => role !== item.value)">
                  {{ item.title }}
                </CommonChip>
              </template>
            </v-select>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" class="cancel" @click="cancelRolesEdit">Cancel</v-btn>
            <v-btn
              data-track="contact.tile-roles.save"
              class="primary"
              type="submit"
              :loading="isRolesUpdating"
            >
              Save Roles
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { omit } from 'lodash-es';

import { AccountLayoutMixin } from '@/bundles/App/mixins/accountMixin';
import { getContactRoles, arrToSnakeCase, ContactRolesEnum } from '@/bundles/Contact/enums/contactRoles';
import { getContactRolesBySource, sourceMappingDictionary } from '@/bundles/Contact/helpers/cards/contactsCard';
import { usePlaceholders } from '@/bundles/App/composables/usePlaceholders';

import CommonChip from '@/bundles/Common/components/chips/CommonChip.vue';
import AppTooltip from '@/bundles/App/components/common/AppTooltip.vue';

export default {
  name: 'ContactTile',

  components: {
    CommonChip,
    AppTooltip
  },

  mixins: [AccountLayoutMixin],

  // Vue 2 kept a parent's class/style on the root and forwarded only real
  // attributes through $attrs; Vue 3 folds class and style into $attrs, so
  // spreading it onto an inner element applied them there AND on the root.
  inheritAttrs: false,

  props: {
    contact: {
      type: Object,
      default: () => ({})
    },
    sourceType: {
      type: String,
      default: ''
    },
    contactRole: {
      type: String,
      default: ''
    },
    isRolesUpdating: Boolean,
    refreshContactEdge: {
      type: String,
      default: null
    },
    expandable: Boolean,
    disableUserActions: Boolean,
  },

  emits: ['updateReferral', 'updateRoles', 'removeContact', 'refresh:contact'],

  setup () {
    const { contactPlaceholder } = usePlaceholders();

    return { contactPlaceholder };
  },

  data: () => ({
    roleDialog: false,
    newRoles: [],
    showDetails: false,
  }),

  computed: {
    // Everything but class/style, which the root keeps.
    innerAttrs () {
      return omit(this.$attrs, ['class', 'style']);
    },
    ...mapGetters('Admin', ['getListByCategory']),
    showEditAction () {
      const sources = ['property', 'company', 'availability', 'pipeline', 'occupier', 'availabilityProperty', 'requirement'];
      const sourceType = sourceMappingDictionary[this.sourceType] || this.sourceType;

      return sources.some(source => source === sourceType);
    },
    isEditDisabled () {
      return ['availabilityProperty', 'sign'].includes(this.sourceType);
    },
    sourceLabel () {
      const sources = ['property', 'company', 'availability', 'pipeline', 'occupier', 'requirement', 'deal', 'comparable', 'pipeline_primary_client', 'availabilityProperty', 'sign'];

      const sourceType = sourceMappingDictionary[this.sourceType] || this.sourceType;

      if (sources.some(source => source === sourceType)) {
        return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
      }

      return '';
    },
    contactRoles () {
      return getContactRolesBySource(this.sourceType);
    },
    memberCompany: function () {
      return this.contact.company?.name || '';
    },
    memberPhone () {
      return this.contact.phone?.direct || '';
    },
    memberPhoneExt () {
      return this.contact.phone?.direct_ext || '';
    },
    memberMobile () {
      return this.contact.phone?.mobile || '';
    },
    memberAvatar: function () {
      return this.contact.img || this.contact.avatar || this.contactPlaceholder;
    },
    memberRoles () {
      return arrToSnakeCase(this.contact.roles || []);
    },
    hasClientAccessRole () {
      return this.memberRoles.includes(ContactRolesEnum.client_access);
    },
    // These mirror the API's clientAccess.js gates (role + linked `member` + `active`) for a
    // working client login. An archived member has its `member` attribute removed rather than
    // nulled, so both "absent" and "null" mean "no linked member" below. There is no chip here:
    // the Contacts card already groups this contact under its "Client Access" role heading, so a
    // chip would only restate it. Only the failure case is worth surfacing.
    isClientAccessContactActive () {
      return this.contact.active === true;
    },
    hasClientAccessMember () {
      return this.contact.member !== undefined && this.contact.member !== null;
    },
    hasClientAccessLogin () {
      return this.contact.is_client === true && this.isClientAccessContactActive && this.hasClientAccessMember;
    },
    showClientAccessWarning () {
      return this.sourceType === 'occupier' && this.hasClientAccessRole && !this.hasClientAccessLogin;
    },
    // Which warning applies: no member ever linked, vs. a linked member that's now archived.
    // Drives both the message and its data-test hook so the two can't drift apart.
    clientAccessWarningVariant () {
      return (this.contact.is_client === true && this.hasClientAccessMember) ? 'archived' : 'no-login';
    },
    clientAccessWarningText () {
      return this.clientAccessWarningVariant === 'archived'
        ? 'This contact is archived — reactivate it to restore client sign-in.'
        : "No client account linked — this contact can't sign in.";
    },
    showReferring () {
      const allowedRoles = [
        ContactRolesEnum.landlord_agent,
        ContactRolesEnum.buyer_agent,
        ContactRolesEnum.seller_agent,
        ContactRolesEnum.tenant_agent,
        ContactRolesEnum.consulting_agent,
      ]
      return allowedRoles.includes(this.contactRole);
    },
    referringStatus: {
      get () {
        if (this.contact.receiving_agent) {
          return 'receiving';
        }
        if (this.contact.referring_agent) {
          return 'referring';
        }
        return '';
      },
      set (value) {
        const payload = {
          _edge: this.contact._edge,
          contactId: this.contact._key,
          referral: value
        }
        this.$emit('updateReferral', payload)
        return true;
      }
    }
  },

  watch: {
    isRolesUpdating (newVal, oldValue) {
      if (!newVal && oldValue) {
        this.roleDialog = false;
      }
    },
    memberRoles: {
      handler (newVal) {
        this.newRoles = newVal;
      },
      deep: true,
      immediate: true,
    }
  },

  methods: {
    getContactRoles (roles) {
      return getContactRoles(roles);
    },
    async saveRoles () {
      const { valid } = await this.$refs.form.validate();

      if (!valid) {
        return;
      }

      this.$emit('updateRoles', { ...this.contact, roles: this.newRoles });
    },
    // drops the unsaved picks, so the dialog reopens with the contact's current roles
    cancelRolesEdit () {
      this.newRoles = [...this.memberRoles];
      this.roleDialog = false;
    },
    editContactRolesClickHandler () {
      if (this.isEditDisabled) {
        return false;
      }

      this.roleDialog = true;
    },
    removeContactClickHandler (key) {
      if (this.isEditDisabled) {
        return false;
      }

      this.$emit('removeContact', key);
    },
  },
}
</script>

<style lang="scss">
.filters-action--disabled {
  // Disabled ink: 38% black in light, 38% white in dark (a fixed black
  // disappeared on the dark menus).
  color: rgba(var(--v-theme-on-surface), var(--v-disabled-opacity)) !important;
}
.contact-tile-card {
  .v-list-item-subtitle {
    overflow: auto !important;
    -webkit-line-clamp: none !important;
    white-space: normal !important;
  }
}
</style>
